"use client";

import { useCallback, useMemo, useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper/types";
import "swiper/css";
import GalleryViewer, {
  type GalleryImage,
} from "@/components/GalleryViewer";
import type { GroupsPackage } from "@/lib/groups/packages";

interface PackagesGallerySectionProps {
  packages: GroupsPackage[];
  /** Maximum photos taken from each package. Defaults to 2. */
  photosPerPackage?: number;
}

interface GallerySlide {
  key: string;
  url: string;
  alt: string;
}

const SECTION_TITLE = "Awesome Gallery";
const SECTION_SUBTITLE = "Group Travel";
const VIEWER_TITLE = "Group Travel Moments";
const VIEWER_CTA = "Group Travel Moments";

/**
 * Build a flat list of slides by taking up to `photosPerPackage` images from
 * each package. Keeps original package order; interleaves so we don't show
 * two photos from the same package back-to-back when possible.
 */
function buildSlides(
  packages: GroupsPackage[],
  photosPerPackage: number,
): GallerySlide[] {
  if (!packages.length || photosPerPackage <= 0) return [];

  const queues = packages.map((p) => ({
    pack: p,
    photos: p.photos.slice(0, photosPerPackage),
  }));

  const slides: GallerySlide[] = [];
  let added = true;
  while (added) {
    added = false;
    for (const q of queues) {
      const url = q.photos.shift();
      if (!url) continue;
      added = true;
      slides.push({
        key: `${q.pack.id}-${slides.length}`,
        url,
        alt: q.pack.packageName,
      });
    }
  }
  return slides;
}

export default function PackagesGallerySection({
  packages,
  photosPerPackage = 2,
}: PackagesGallerySectionProps) {
  const slides = useMemo(
    () => buildSlides(packages, photosPerPackage),
    [packages, photosPerPackage],
  );

  // Map slide list to the viewer's image shape. Memoised so the viewer's
  // `images` prop reference is stable across re-renders.
  const viewerImages = useMemo<GalleryImage[]>(
    () =>
      slides.map((s) => ({
        id: s.key,
        src: s.url,
        alt: s.alt,
      })),
    [slides],
  );

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const openViewer = useCallback((index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  }, []);

  const closeViewer = useCallback(() => setViewerOpen(false), []);

  // Open the viewer on a genuine tap. Swiper's `click` event only fires when the
  // pointer didn't drag (so swiping the carousel never triggers it), which is
  // why the per-slide overlay can be non-interactive — letting drag through.
  // `data-swiper-slide-index` maps loop clones back to the real slide index.
  const handleSlideClick = useCallback(
    (swiper: SwiperClass) => {
      const raw = swiper.clickedSlide?.getAttribute("data-swiper-slide-index");
      const idx = raw != null ? Number(raw) : swiper.clickedIndex;
      if (Number.isInteger(idx) && idx >= 0 && idx < slides.length) {
        openViewer(idx);
      }
    },
    [openViewer, slides.length],
  );

  // No images → skip rendering the entire section.
  if (slides.length === 0) return null;

  return (
    <div className="space px-xl-5 px-2" id="gallery-sec">
      <div className="container mb-30">
        <div className="row gy-4 justify-content-between align-items-center">
          <div className="col-lg-6">
            <div className="title-area mb-20 text-center text-lg-start">
              <span className="sub-title">{SECTION_SUBTITLE}</span>
              <h2 className="sec-title">{SECTION_TITLE}</h2>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="d-flex justify-content-center justify-content-lg-end">
              <button
                type="button"
                className="gallery-viewer-cta"
                onClick={() => openViewer(0)}
                aria-label={`Open ${VIEWER_CTA.toLowerCase()} viewer`}
              >
                <i
                  className="fa-solid fa-expand gallery-viewer-cta__icon"
                  aria-hidden="true"
                />
                <span className="gallery-viewer-cta__label">{VIEWER_CTA}</span>
                <i
                  className="fa-solid fa-arrow-right gallery-viewer-cta__arrow"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-init slide sizing — without these rules, every slide briefly
          renders at 100% of the container width before Swiper applies its
          inline `style="width: NNNpx"`, which makes the images look huge
          for a few frames and then collapse. The square aspect-ratio is
          set on `.gallery-insta` (the actual image wrapper) so the image
          fills the reserved box edge-to-edge. */}
      <style>{`
        #gallerySlider1 .swiper-slide {
          flex-shrink: 0;
          width: 80%;
          height: auto;
        }
        @media (min-width: 576px) { #gallerySlider1 .swiper-slide { width: 50%; } }
        @media (min-width: 768px) { #gallerySlider1 .swiper-slide { width: 33.3333%; } }
        @media (min-width: 992px) { #gallerySlider1 .swiper-slide { width: 25%; } }
        #gallerySlider1 .gallery-insta {
          position: relative;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          cursor: zoom-in;
        }
        /* next/image fill mode renders an absolutely-positioned img.
           The !important rules defeat the Themeforest template's blanket
           ".gallery-insta img { width: 100%; height: 100% }" rule that
           would otherwise stretch the photo. */
        #gallerySlider1 .gallery-insta > img {
          position: absolute !important;
          inset: 0 !important;
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          object-position: center !important;
          display: block;
          transition: transform 0.5s ease;
        }
        #gallerySlider1 .gallery-insta:hover > img {
          transform: scale(1.06);
        }
        /* Hover overlay with maximize icon — purely decorative. It must not
           capture pointer events, or it would swallow Swiper's drag. */
        #gallerySlider1 .gallery-insta__expand {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(180deg,
            rgba(0,0,0,0) 0%,
            rgba(0,0,0,0.35) 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
          color: #fff;
        }
        #gallerySlider1 .gallery-insta:hover .gallery-insta__expand {
          opacity: 1;
        }
        #gallerySlider1 .gallery-insta__expand-badge {
          width: 56px;
          height: 56px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.18);
          border: 1px solid rgba(255,255,255,0.5);
          color: #fff;
          font-size: 20px;
          border-radius: 999px;
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          transform: scale(0.85);
          transition: transform 0.3s ease, background 0.2s ease;
        }
        #gallerySlider1 .gallery-insta:hover .gallery-insta__expand-badge {
          transform: scale(1);
          background: var(--theme-color, #e8604c);
          border-color: var(--theme-color, #e8604c);
        }

        /* "Group Travel Moments" CTA pill in the section header */
        .gallery-viewer-cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 10px 22px 10px 18px;
          background: linear-gradient(135deg,
            var(--theme-color, #e8604c) 0%,
            #ff8a65 100%);
          color: #fff;
          border: 0;
          border-radius: 999px;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          cursor: pointer;
          box-shadow: 0 6px 18px rgba(232, 96, 76, 0.28);
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            gap 0.2s ease;
          font-family: inherit;
        }
        .gallery-viewer-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 22px rgba(232, 96, 76, 0.4);
          gap: 14px;
        }
        .gallery-viewer-cta:focus-visible {
          outline: 2px solid var(--theme-color, #e8604c);
          outline-offset: 3px;
        }
        .gallery-viewer-cta__icon {
          font-size: 14px;
        }
        .gallery-viewer-cta__arrow {
          font-size: 12px;
          opacity: 0.9;
          transition: transform 0.2s ease;
        }
        .gallery-viewer-cta:hover .gallery-viewer-cta__arrow {
          transform: translateX(3px);
        }
      `}</style>

      <div className="container-fluid">
        <Swiper
          id="gallerySlider1"
          modules={[Autoplay]}
          spaceBetween={0}
          slidesPerView={1.25}
          loop={slides.length > 4}
          speed={800}
          grabCursor
          autoplay={{ delay: 4500, disableOnInteraction: false }}
          onClick={handleSlideClick}
          breakpoints={{
            576: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            992: { slidesPerView: 4 },
          }}
        >
          {slides.map((slide) => (
            <SwiperSlide key={slide.key}>
              <div className="gallery-insta style2">
                <Image
                  src={slide.url}
                  alt={slide.alt}
                  fill
                  sizes="(max-width: 575px) 80vw, (max-width: 767px) 50vw, (max-width: 991px) 33vw, 25vw"
                  loading="lazy"
                  style={{ objectFit: "cover" }}
                />
                {/* Visual hover hint only — non-interactive so dragging the
                    carousel works; the tap-to-open is handled by Swiper's
                    onClick above. */}
                <span className="gallery-insta__expand" aria-hidden="true">
                  <span className="gallery-insta__expand-badge">
                    <i className="fa-solid fa-expand" />
                  </span>
                </span>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <GalleryViewer
        open={viewerOpen}
        images={viewerImages}
        initialIndex={viewerIndex}
        onClose={closeViewer}
        title={VIEWER_TITLE}
      />
    </div>
  );
}
